import React from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import { tokenFootprintLayout } from '../utils/tokenVisualGeometry';
import { TARGET_TONES, rangeOutline } from '../utils/targetingPreview';

/**
 * While choosing a target, outlines what the attack or spell can reach from its user's token: reach and normal range
 * solid, long range dashed. Hovering a creature colors the outlines and that creature by how the roll would go
 * (see targetingPreview.TARGET_TONES) and names why beside it.
 */
export default function TargetRangePreview({ preview, hovered, targetId, tokens, gridColumns, gridRows }) {
  const source = tokens.find((token) => token?.characterId === preview?.sourceId && token.position);
  if (!source || !Array.isArray(preview.rings) || !preview.rings.length) return null;
  const { anchor, size: squares } = tokenFootprintLayout(source, source.position, gridColumns, gridRows);
  const tone = hovered?.tone || 'idle';
  const shine = Boolean(TARGET_TONES[tone]?.shine);
  const target = targetId ? tokens.find((token) => token?.characterId === targetId && token.position) : null;
  const targetLayout = target ? tokenFootprintLayout(target, target.position, gridColumns, gridRows) : null;
  return (
    <>
      <svg className={classNames('target-range-preview', `target-range-preview--${tone}`, shine && 'target-range-preview--shine')}
        data-testid="target-range-preview" data-tone={tone} aria-hidden="true" viewBox={`0 0 ${gridColumns} ${gridRows}`} preserveAspectRatio="none">
        {preview.rings.map((ring) => {
          const box = rangeOutline({ anchor, squares, feet: ring.feet });
          return <rect key={`${ring.kind}-${ring.feet}`} className={`target-range-preview__ring target-range-preview__ring--${ring.kind}`}
            data-feet={ring.feet} x={box.x} y={box.y} width={box.size} height={box.size} rx={Math.min(0.4, box.size / 8)} vectorEffect="non-scaling-stroke" />;
        })}
      </svg>
      {targetLayout && hovered?.description ? (
        <div className={classNames('target-range-preview__label', `target-range-preview__label--${tone}`, shine && 'target-range-preview__label--shine')}
          role="status" style={{ left: `${targetLayout.center.x * 100}%`, top: `${(targetLayout.center.y - targetLayout.height / 2) * 100}%` }}>
          {hovered.description}
        </div>
      ) : null}
    </>
  );
}

TargetRangePreview.propTypes = {
  preview: PropTypes.shape({ sourceId: PropTypes.string, rings: PropTypes.arrayOf(PropTypes.shape({ feet: PropTypes.number, kind: PropTypes.string })) }),
  hovered: PropTypes.shape({ tone: PropTypes.string, description: PropTypes.string }),
  targetId: PropTypes.string,
  tokens: PropTypes.arrayOf(PropTypes.object).isRequired,
  gridColumns: PropTypes.number.isRequired,
  gridRows: PropTypes.number.isRequired,
};
